import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty } from './client';
import { keys } from './keys';
import { invalidateCommunityAudit } from './communityAudit';
import { CostedLinesSchema, LineOverrideHistoryListSchema, LinesListSchema } from './schemas';

export type LineOverrideKind = 'qty' | 'price' | 'manual_cost';

function invalidateLaborRelated(
  qc: ReturnType<typeof useQueryClient>,
  mtoId: string | undefined,
  communityId: string | undefined
) {
  void qc.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey[0] === 'mtos' && q.queryKey[2] === 'costed-lines',
  });
  if (mtoId) {
    void qc.invalidateQueries({ queryKey: keys.mtoLines(mtoId) });
  }
  if (communityId) {
    void qc.invalidateQueries({ queryKey: keys.communityUnpriced(communityId) });
    void qc.invalidateQueries({ queryKey: ['communities', communityId, 'labor-rates'] });
    void qc.invalidateQueries({ queryKey: keys.laborAnalysis(communityId) });
    void qc.invalidateQueries({ queryKey: ['communities', communityId, 'bid'] });
    void qc.invalidateQueries({ queryKey: keys.communityMtoQuantities(communityId) });
    invalidateCommunityAudit(qc, communityId);
  }
}

function historyPath(lineId: number, kind: LineOverrideKind): string {
  if (kind === 'qty') return `/api/mto-lines/${lineId}/qty-override/history`;
  if (kind === 'price') return `/api/mto-lines/${lineId}/price-override/history`;
  return `/api/mto-lines/${lineId}/manual-cost/history`;
}

function savePath(lineId: number, kind: LineOverrideKind): string {
  if (kind === 'qty') return `/api/mto-lines/${lineId}/qty-override`;
  if (kind === 'price') return `/api/mto-lines/${lineId}/price-override`;
  return `/api/mto-lines/${lineId}/manual-cost`;
}

function clearPath(lineId: number, kind: LineOverrideKind): string {
  if (kind === 'qty') return `/api/mto-lines/${lineId}/qty-override`;
  if (kind === 'price') return `/api/mto-lines/${lineId}/price-override`;
  return `/api/mto-lines/${lineId}/manual-cost`;
}

export function useMtoLines(mtoId: string | undefined) {
  return useQuery({
    queryKey: keys.mtoLines(mtoId ?? ''),
    enabled: !!mtoId,
    queryFn: () => apiFetch(`/api/mtos/${mtoId}/lines`, LinesListSchema),
  });
}

export function useCostedLines(mtoId: string | undefined, asOf?: string) {
  const base = `/api/mtos/${mtoId}/costed-lines`;
  const path = asOf ? `${base}?asOf=${encodeURIComponent(asOf)}` : base;
  return useQuery({
    queryKey: keys.costedLines(mtoId ?? '', asOf),
    enabled: !!mtoId,
    queryFn: () => apiFetch(path, CostedLinesSchema),
  });
}

export function useLineOverrideHistory(lineId: number | null, kind: LineOverrideKind | null) {
  return useQuery({
    queryKey: ['mto-lines', lineId, kind, 'history'],
    enabled: lineId != null && kind != null,
    queryFn: () => apiFetch(historyPath(lineId ?? 0, kind ?? 'qty'), LineOverrideHistoryListSchema),
  });
}

export type ManualLineHistoryKind = 'qty' | 'price';

function manualHistoryPath(lineId: number, kind: ManualLineHistoryKind): string {
  if (kind === 'qty') return `/api/manual-mto-lines/${lineId}/qty-history`;
  return `/api/manual-mto-lines/${lineId}/unit-cost-history`;
}

export function useManualMtoLineHistory(
  lineId: number | null,
  kind: ManualLineHistoryKind | null
) {
  return useQuery({
    queryKey: ['manual-mto-lines', lineId, kind, 'history'],
    enabled: lineId != null && kind != null,
    queryFn: () =>
      apiFetch(manualHistoryPath(lineId ?? 0, kind ?? 'qty'), LineOverrideHistoryListSchema),
  });
}

export function useSaveLineOverride(
  mtoId: string | undefined,
  communityId: string | undefined,
  kind: LineOverrideKind
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { lineId: number; value: number; effective_date: string }) => {
      const payload =
        kind === 'qty'
          ? { qty: body.value, effective_date: body.effective_date }
          : kind === 'price'
            ? { unit_price: body.value, effective_date: body.effective_date }
            : { unit_cost: body.value, effective_date: body.effective_date };
      return apiFetchEmpty(savePath(body.lineId, kind), {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => invalidateLaborRelated(qc, mtoId, communityId),
  });
}

const SameItemMtoSchema = z.object({
  mto_file_id: z.coerce.number(),
  mto_name: z.string(),
  line_ids: z.array(z.coerce.number()),
  qty: z.coerce.number().nullable().optional(),
  price_override: z.coerce.number().nullable().optional(),
});

const SameItemMtosResponseSchema = z.object({
  community_id: z.coerce.number().optional(),
  item: z.string(),
  payable_task_group: z.string(),
  mtos: z.array(SameItemMtoSchema),
});

export type SameItemMto = z.infer<typeof SameItemMtoSchema>;

export function useSameItemMtos(lineId: number | null, enabled = true) {
  return useQuery({
    queryKey: keys.sameItemMtos(lineId ?? 0),
    enabled: enabled && lineId != null && lineId > 0,
    queryFn: () =>
      apiFetch(`/api/mto-lines/${lineId}/same-item-mtos`, SameItemMtosResponseSchema),
  });
}

export function useBulkSavePriceOverride(
  mtoId: string | undefined,
  communityId: string | undefined
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { line_ids: number[]; unit_price: number; effective_date: string }) =>
      apiFetchEmpty('/api/mto-lines/price-override/bulk', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateLaborRelated(qc, mtoId, communityId),
  });
}

export function useClearLineOverride(
  mtoId: string | undefined,
  communityId: string | undefined,
  kind: LineOverrideKind
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) =>
      apiFetchEmpty(clearPath(lineId, kind), { method: 'DELETE' }),
    onSuccess: () => invalidateLaborRelated(qc, mtoId, communityId),
  });
}

export function useHideLaborLine(mtoId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) =>
      apiFetchEmpty('/api/labor/hide', { method: 'POST', body: JSON.stringify({ lineId }) }),
    onSuccess: () => invalidateLaborRelated(qc, mtoId, communityId),
  });
}

export function useUnhideLaborLine(mtoId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: number) =>
      apiFetchEmpty('/api/labor/unhide', { method: 'POST', body: JSON.stringify({ lineId }) }),
    onSuccess: () => invalidateLaborRelated(qc, mtoId, communityId),
  });
}

export function useHideLaborItemByName(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemName: string) =>
      apiFetchEmpty(`/api/communities/${communityId}/labor/hide-item`, {
        method: 'POST',
        body: JSON.stringify({ itemName }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.communityUnpriced(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: ['communities', communityId, 'labor-rates'] });
      void qc.invalidateQueries({ queryKey: keys.laborAnalysis(communityId ?? '') });
      invalidateCommunityAudit(qc, communityId);
    },
  });
}

export function useUnhideLaborItemByName(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemName: string) =>
      apiFetchEmpty(`/api/communities/${communityId}/labor/unhide-item`, {
        method: 'POST',
        body: JSON.stringify({ itemName }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.communityUnpriced(communityId ?? '') });
      void qc.invalidateQueries({ queryKey: ['communities', communityId, 'labor-rates'] });
      invalidateCommunityAudit(qc, communityId);
    },
  });
}

export type ManualMtoLineInput = {
  mto_file_id: number;
  qty?: number | null;
  item_id: string;
  ref_num?: string | null;
  payable_task_group?: string | null;
  unit_cost?: number | null;
  effective_date?: string;
  /** Required on create; omitted on edit. */
  cost_kind?: 'material' | 'labor';
};

const ManualLineCreateResultSchema = z.object({
  id: z.coerce.number(),
  success: z.boolean().optional(),
});

export function useCreateManualMtoLine(mtoId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ManualMtoLineInput) =>
      apiFetch('/api/manual-mto-lines', ManualLineCreateResultSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      invalidateLaborRelated(qc, mtoId, communityId);
      void qc.invalidateQueries({ queryKey: ['manual-mto-lines'] });
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      if (communityId) {
        void qc.invalidateQueries({ queryKey: keys.communityMtos(communityId) });
      }
    },
  });
}

export type BulkManualMtoLineInput = Omit<ManualMtoLineInput, 'mto_file_id'> & {
  mto_file_ids: number[];
};

export function useCreateManualMtoLinesBulk(communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ mto_file_ids, ...line }: BulkManualMtoLineInput) => {
      const ids: number[] = [];
      for (const mto_file_id of mto_file_ids) {
        const row = await apiFetch('/api/manual-mto-lines', ManualLineCreateResultSchema, {
          method: 'POST',
          body: JSON.stringify({ ...line, mto_file_id }),
        });
        ids.push(row.id);
      }
      return { ids, count: ids.length };
    },
    onSuccess: () => {
      invalidateLaborRelated(qc, undefined, communityId);
      void qc.invalidateQueries({ queryKey: ['manual-mto-lines'] });
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      if (communityId) {
        void qc.invalidateQueries({ queryKey: keys.communityMtos(communityId) });
      }
    },
  });
}

export type UpdateManualMtoLineInput = {
  id: number;
  qty?: number | null;
  item_id: string;
  ref_num?: string | null;
  payable_task_group?: string | null;
  unit_cost?: number | null;
  effective_date?: string;
};

export function useUpdateManualMtoLine(mtoId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateManualMtoLineInput) =>
      apiFetch(
        `/api/manual-mto-lines/${id}`,
        z.object({ id: z.coerce.number(), success: z.boolean().optional() }),
        { method: 'PUT', body: JSON.stringify(body) }
      ),
    onSuccess: () => {
      invalidateLaborRelated(qc, mtoId, communityId);
      void qc.invalidateQueries({ queryKey: ['manual-mto-lines'] });
    },
  });
}

export function useDeleteManualMtoLine(mtoId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetchEmpty(`/api/manual-mto-lines/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateLaborRelated(qc, mtoId, communityId);
      void qc.invalidateQueries({ queryKey: ['manual-mto-lines'] });
    },
  });
}
